import { useState, useEffect } from "react";
import SalesProjection from "./SalesProjection";
import { InvestorDashboard } from "./components/dashboard/InvestorDashboard.jsx";

const API = "https://web-production-61310.up.railway.app";
const ORANGE = "#FF6B00";
const DARK = "#0d0d0d";
const CARD_BORDER = "#222";
const TEXT_DIM = "#555";
const TEXT_MID = "#888";

const fmt = (n) => Math.round(n).toLocaleString("es-AR");
const fmtUSD = (n) => Math.round(n).toLocaleString("en-US");
const fmtMoney = (n, currency) => currency === "USD" ? fmtUSD(n) : fmt(n);
const currSymbol = (currency) => currency === "USD" ? "USD " : "$";
const tempUnit = (currency) => currency === "USD" ? "°F" : "°C";

async function fetchClima(lat, lon, currency) {
  const units = currency === "USD" ? "&temperature_unit=fahrenheit" : "";
  const tz = currency === "USD" ? "America%2FNew_York" : "America%2FArgentina%2FBuenos_Aires";
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weathercode,precipitation&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=${tz}&past_days=6&forecast_days=1${units}`;
  const r = await fetch(url);
  return r.json();
}

function climaInfo(code) {
  if (code === 0) return { tipo: "sol", label: "Despejado", lluvia: false };
  if (code <= 2) return { tipo: "parcial", label: "Parcial", lluvia: false };
  if (code <= 3) return { tipo: "nublado", label: "Nublado", lluvia: false };
  if (code <= 48) return { tipo: "nublado", label: "Neblina", lluvia: false };
  if (code <= 67) return { tipo: "lluvia", label: "Lluvia", lluvia: true };
  if (code <= 77) return { tipo: "nieve", label: "Nieve", lluvia: false };
  if (code <= 82) return { tipo: "lluvia", label: "Lluvia", lluvia: true };
  return { tipo: "tormenta", label: "Tormenta", lluvia: true };
}

function ClimaIcono({ code, size = 20 }) {
  const { tipo } = climaInfo(code);
  const s = size;
  if (tipo === "sol") return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="5" fill="#FFD93D" />
      {[0,45,90,135,180,225,270,315].map((deg, i) => (
        <line key={i} x1="12" y1="3.5" x2="12" y2="1.5" stroke="#FFD93D" strokeWidth="2" strokeLinecap="round" transform={`rotate(${deg} 12 12)`} />
      ))}
    </svg>
  );
  if (tipo === "parcial") return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="9" r="3.5" fill="#FFD93D" />
      {[0,60,120,180,240,300].map((deg, i) => (
        <line key={i} x1="9" y1="3" x2="9" y2="1.5" stroke="#FFD93D" strokeWidth="1.8" strokeLinecap="round" transform={`rotate(${deg} 9 9)`} />
      ))}
      <path d="M8 17 Q8 14 11 14 Q12 11 15 12 Q17 12 17 14 Q19 14 19 16 Q19 18 17 18 H9 Q8 18 8 17 Z" fill="#aaa" />
    </svg>
  );
  if (tipo === "lluvia") return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M3 13 Q3 9 7 9 Q8 5 12 6 Q15 6 15 9 Q18 9 18 12 Q18 15 15 15 H5 Q3 15 3 13 Z" fill="#777" />
      <line x1="7" y1="18" x2="6" y2="21" stroke="#6bb5ff" strokeWidth="2" strokeLinecap="round"/>
      <line x1="11" y1="18" x2="10" y2="21" stroke="#6bb5ff" strokeWidth="2" strokeLinecap="round"/>
      <line x1="15" y1="18" x2="14" y2="21" stroke="#6bb5ff" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
  if (tipo === "tormenta") return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M3 12 Q3 8 7 8 Q8 4 12 5 Q15 5 15 8 Q18 8 18 11 Q18 14 15 14 H5 Q3 14 3 12 Z" fill="#666" />
      <polyline points="10,14 8,18 11,18 9,22" stroke="#FFD93D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M4 15 Q4 11 8 11 Q9 7 13 8 Q16 8 16 11 Q19 11 19 14 Q19 17 16 17 H6 Q4 17 4 15 Z" fill="#888" />
    </svg>
  );
}

function WaveIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M5 19 Q15 15 15 12 Q15 9 5 5" stroke="#fff" strokeWidth={2.5} strokeLinecap="round" opacity="0.2"/>
      <path d="M7 16 Q13 13 13 12 Q13 11 7 8" stroke="#fff" strokeWidth={2.5} strokeLinecap="round" opacity="0.55"/>
      <path d="M9 14 Q11 13 11 12 Q11 11 9 10" stroke={ORANGE} strokeWidth={2.5} strokeLinecap="round"/>
      <circle cx="6.5" cy="12" r="3" fill={ORANGE}/>
    </svg>
  );
}

function AnimatedNumber({ value, prefix = "" }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let cur = 0;
    const step = value / (900 / 16);
    const t = setInterval(() => {
      cur += step;
      if (cur >= value) { setDisplay(value); clearInterval(t); }
      else setDisplay(Math.floor(cur));
    }, 16);
    return () => clearInterval(t);
  }, [value]);
  return <span>{prefix}{fmt(display)}</span>;
}

function Chip({ pct }) {
  if (pct === 0) return null;
  return pct >= 0
    ? <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:"rgba(74,222,128,.12)",color:"#4ade80",border:"1px solid rgba(74,222,128,.2)",padding:"2px 7px",borderRadius:99,fontSize:10,fontWeight:700 }}>↑ {pct}%</span>
    : <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:"rgba(239,68,68,.12)",color:"#ef4444",border:"1px solid rgba(239,68,68,.2)",padding:"2px 7px",borderRadius:99,fontSize:10,fontWeight:700 }}>↓ {Math.abs(pct)}%</span>;
}


// ── GOOGLE LOGIN SCREEN ─────────────────────────────────────────
const GOOGLE_CLIENT_ID = "446871755730-m8u7ar878f5khd520h9ol5e236ul5u3e.apps.googleusercontent.com";

function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPw, setConfirmNewPw] = useState("");

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) { setResetToken(token); setMode("reset"); window.history.replaceState({}, "", window.location.pathname); }
  }, []);

  // Google Sign-In
  useEffect(() => {
    if (mode !== "login") return;
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          setLoading(true); setError("");
          try {
            const r = await fetch(`${API}/auth/google/login`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ credential: response.credential }),
            });
            const data = await r.json();
            if (data.ok) { localStorage.setItem("rm_user", JSON.stringify(data)); localStorage.setItem("rm_login_time", Date.now().toString()); onLogin(data); }
            else { setError(data.detail || "Error de autenticación"); }
          } catch (e) { setError("Error de conexión"); }
          setLoading(false);
        },
      });
      const el = document.getElementById("google-btn");
      if (el) {
        window.google.accounts.id.renderButton(el,
          { theme: "filled_black", size: "large", shape: "pill", text: "signin_with", width: 280, locale: "es" }
        );
      }
    };
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch(e) {} };
  }, [onLogin, mode]);

  const handleEmailLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Email o contraseña incorrectos");
      localStorage.setItem("rm_user", JSON.stringify(data)); localStorage.setItem("rm_login_time", Date.now().toString());
      onLogin(data);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError("");
    if (password !== confirmPw) { setError("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, nombre }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Error al registrar");
      localStorage.setItem("rm_user", JSON.stringify(data)); localStorage.setItem("rm_login_time", Date.now().toString());
      onLogin(data);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); setLoading(true); setError(""); setSuccess("");
    try {
      const r = await fetch(`${API}/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Error");
      setSuccess(data.message);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault(); setError("");
    if (newPassword !== confirmNewPw) { setError("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, password: newPassword }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Error al restablecer");
      setSuccess(data.message);
      setTimeout(() => { setMode("login"); setSuccess(""); }, 2000);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const inputStyle = {
    width: "100%", maxWidth: 280, padding: "12px 16px", borderRadius: 10,
    border: `1px solid ${CARD_BORDER}`, background: "#161616", color: "#fff",
    fontSize: 14, outline: "none", marginBottom: 10, boxSizing: "border-box",
    fontFamily: "'DM Sans',sans-serif",
  };
  const btnStyle = {
    width: "100%", maxWidth: 280, padding: "12px", borderRadius: 10, border: "none",
    background: ORANGE, color: "#fff", fontSize: 14, fontWeight: 600,
    cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
    opacity: loading ? 0.6 : 1,
  };
  const linkStyle = { color: TEXT_MID, fontSize: 12, cursor: "pointer", textDecoration: "underline", background: "none", border: "none", padding: 0, fontFamily: "'DM Sans',sans-serif" };
  const pageStyle = { minHeight: "100vh", background: DARK, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, padding: 24, fontFamily: "'DM Sans',sans-serif" };

  // ── RESET PASSWORD ──
  if (mode === "reset") {
    return (
      <div style={pageStyle}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
        <WaveIcon size={48} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, color: "#fff" }}>resto<span style={{ color: ORANGE, fontWeight: 300 }}>mático</span></div>
          <div style={{ fontSize: 15, color: "#fff", marginTop: 16, fontWeight: 600 }}>Nueva contraseña</div>
          <div style={{ fontSize: 12, color: TEXT_DIM, marginTop: 4 }}>Ingresá tu nueva contraseña</div>
        </div>
        {error && <div style={{ fontSize: 12, color: "#ef4444" }}>{error}</div>}
        {success && <div style={{ fontSize: 12, color: "#4ade80" }}>{success}</div>}
        <form onSubmit={handleResetPassword} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <input type="password" placeholder="Nueva contraseña" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} required minLength={8} />
          <input type="password" placeholder="Confirmar contraseña" value={confirmNewPw} onChange={e => setConfirmNewPw(e.target.value)} style={inputStyle} required />
          <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Guardando..." : "Guardar contraseña"}</button>
        </form>
      </div>
    );
  }

  // ── FORGOT PASSWORD ──
  if (mode === "forgot") {
    return (
      <div style={pageStyle}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
        <WaveIcon size={48} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, color: "#fff" }}>resto<span style={{ color: ORANGE, fontWeight: 300 }}>mático</span></div>
          <div style={{ fontSize: 15, color: "#fff", marginTop: 16, fontWeight: 600 }}>¿Olvidaste tu contraseña?</div>
          <div style={{ fontSize: 12, color: TEXT_DIM, marginTop: 4 }}>Te enviamos un link para restablecerla</div>
        </div>
        {error && <div style={{ fontSize: 12, color: "#ef4444" }}>{error}</div>}
        {success && <div style={{ fontSize: 12, color: "#4ade80" }}>{success}</div>}
        <form onSubmit={handleForgotPassword} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <input type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} required />
          <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Enviando..." : "Enviar link"}</button>
        </form>
        <button onClick={() => { setMode("login"); setError(""); setSuccess(""); }} style={linkStyle}>← Volver al login</button>
      </div>
    );
  }

  // ── REGISTER ──
  if (mode === "register") {
    return (
      <div style={pageStyle}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
        <WaveIcon size={48} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, color: "#fff" }}>resto<span style={{ color: ORANGE, fontWeight: 300 }}>mático</span></div>
          <div style={{ fontSize: 15, color: "#fff", marginTop: 16, fontWeight: 600 }}>Crear cuenta</div>
          <div style={{ fontSize: 12, color: TEXT_DIM, marginTop: 4 }}>Registrate para acceder a tus locales</div>
        </div>
        {error && <div style={{ fontSize: 12, color: "#ef4444" }}>{error}</div>}
        <form onSubmit={handleRegister} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <input type="text" placeholder="Nombre (opcional)" value={nombre} onChange={e => setNombre(e.target.value)} style={inputStyle} />
          <input type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} required />
          <input type="password" placeholder="Contraseña (mín. 8 caracteres)" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} required minLength={8} />
          <input type="password" placeholder="Confirmar contraseña" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} style={inputStyle} required />
          <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Creando cuenta..." : "Crear cuenta"}</button>
        </form>
        <button onClick={() => { setMode("login"); setError(""); }} style={linkStyle}>← Ya tengo cuenta</button>
      </div>
    );
  }

  // ── LOGIN ──
  return (
    <div style={pageStyle}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
      <WaveIcon size={48} />
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 28, color: "#fff" }}>resto<span style={{ color: ORANGE, fontWeight: 300 }}>mático</span></div>
        <div style={{ fontSize: 13, color: TEXT_DIM, marginTop: 6 }}>Dashboard de ventas en tiempo real</div>
      </div>
      <div id="google-btn" style={{ minHeight: 44 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", maxWidth: 280 }}>
        <div style={{ flex: 1, height: 1, background: CARD_BORDER }} />
        <span style={{ color: TEXT_DIM, fontSize: 11 }}>o con email</span>
        <div style={{ flex: 1, height: 1, background: CARD_BORDER }} />
      </div>
      <form onSubmit={handleEmailLogin} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
        <input type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} required />
        <input type="password" placeholder="Contraseña" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} required />
        <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Ingresando..." : "Iniciar sesión"}</button>
      </form>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", maxWidth: 280 }}>
        <button onClick={() => { setMode("forgot"); setError(""); }} style={linkStyle}>¿Olvidaste tu contraseña?</button>
        <button onClick={() => { setMode("register"); setError(""); }} style={linkStyle}>Crear cuenta</button>
      </div>
      {loading && <div style={{ fontSize: 12, color: TEXT_DIM }}>Verificando...</div>}
      {error && <div style={{ fontSize: 12, color: "#ef4444" }}>{error}</div>}
    </div>
  );
}

// ── LOCAL SELECTOR ──────────────────────────────────────────────
function LocalSelector({ user, locales, onSelect, onLogout, onAdmin }) {
  return (
    <div style={{ minHeight: "100vh", background: DARK, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 24, fontFamily: "'DM Sans',sans-serif", padding: 24 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {user.avatar && <img src={user.avatar} alt="" style={{ width: 36, height: 36, borderRadius: "50%", border: `2px solid ${CARD_BORDER}` }} referrerPolicy="no-referrer" />}
        <div>
          <div style={{ fontSize: 14, color: "#fff", fontWeight: 600 }}>{user.nombre}</div>
          <div style={{ fontSize: 11, color: TEXT_DIM }}>{user.email}</div>
        </div>
      </div>
      <WaveIcon size={36} />
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 22, color: "#fff" }}>resto<span style={{ color: ORANGE, fontWeight: 300 }}>mático</span></div>
      </div>
      {locales.length === 0 ? (
        <div style={{ textAlign: "center", maxWidth: 300 }}>
          <div style={{ fontSize: 14, color: TEXT_MID, marginBottom: 8 }}>Tu cuenta está pendiente de activación</div>
          <div style={{ fontSize: 12, color: TEXT_DIM }}>El administrador te asignará tus locales pronto.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 320 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: TEXT_DIM, letterSpacing: "2px", textAlign: "center" }}>SELECCIONÁ TU LOCAL</div>
          {locales.map(l => (
            <button key={l.slug} onClick={() => onSelect(l)}
              style={{ width: "100%", padding: "16px 20px", background: "#161616", border: `1px solid ${CARD_BORDER}`, borderRadius: 14, color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'DM Sans',sans-serif" }}>
              <div style={{ textAlign: "left" }}>
                <div>{l.nombre}</div>
                <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 2 }}>{l.currency === "USD" ? "🇺🇸" : "🇦🇷"} {l.pos_type?.toUpperCase()}</div>
              </div>
              <span style={{ color: ORANGE, fontSize: 18 }}>→</span>
            </button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
        {user.role === "superadmin" && (
          <button onClick={onAdmin} style={{ padding: "8px 20px", background: "none", border: `1px solid ${CARD_BORDER}`, borderRadius: 10, color: TEXT_MID, fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Admin Panel</button>
        )}
        <button onClick={onLogout} style={{ padding: "8px 20px", background: "none", border: `1px solid ${CARD_BORDER}`, borderRadius: 10, color: TEXT_DIM, fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Cerrar sesión</button>
      </div>
    </div>
  );
}

// ── ADMIN PANEL ─────────────────────────────────────────────────
function AdminPanel({ user, onBack }) {
  const [users, setUsers] = useState([]);
  const [allLocales, setAllLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [ru, rl] = await Promise.all([
        fetch(`${API}/admin/users?email=${encodeURIComponent(user.email)}`).then(r => r.json()),
        fetch(`${API}/admin/locales?admin_pin=0708`).then(r => r.json()),
      ]);
      setUsers(ru.users || []); setAllLocales(rl.locales || []);
    } catch (e) { setMsg("Error de conexión"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const assignLocale = async (userId, localId) => {
    await fetch(`${API}/admin/users/${userId}/locales?email=${encodeURIComponent(user.email)}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ local_id: localId }),
    });
    setMsg("Local asignado"); load();
  };
  const revokeLocale = async (userId, localId) => {
    await fetch(`${API}/admin/users/${userId}/locales/${localId}?email=${encodeURIComponent(user.email)}`, { method: "DELETE" });
    setMsg("Local revocado"); load();
  };

  return (
    <div style={{ minHeight: "100vh", background: DARK, padding: 24, fontFamily: "'DM Sans',sans-serif", color: "#fff" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <WaveIcon size={28} />
            <span style={{ fontSize: 18 }}>resto<span style={{ color: ORANGE, fontWeight: 300 }}>mático</span> <span style={{ color: TEXT_DIM, fontSize: 13 }}>admin</span></span>
          </div>
          <button onClick={onBack} style={{ padding: "8px 16px", background: "none", border: `1px solid ${CARD_BORDER}`, borderRadius: 10, color: TEXT_MID, fontSize: 12, cursor: "pointer" }}>← Volver</button>
        </div>
        {msg && <div style={{ padding: "10px 16px", background: "rgba(74,222,128,.1)", border: "1px solid rgba(74,222,128,.2)", borderRadius: 10, color: "#4ade80", fontSize: 12, marginBottom: 16 }}>{msg}</div>}
        {loading ? <div style={{ color: TEXT_DIM, fontSize: 13 }}>Cargando...</div> : (
          <>
            <div style={{ fontSize: 10, fontWeight: 800, color: TEXT_DIM, letterSpacing: "2px", marginBottom: 16 }}>USUARIOS REGISTRADOS ({users.length})</div>
            {users.map(u => (
              <div key={u.id} style={{ background: "#161616", border: `1px solid ${CARD_BORDER}`, borderRadius: 14, padding: 18, marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  {u.avatar && <img src={u.avatar} alt="" style={{ width: 32, height: 32, borderRadius: "50%" }} referrerPolicy="no-referrer" />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{u.nombre || u.email}</div>
                    <div style={{ fontSize: 11, color: TEXT_DIM }}>{u.email}</div>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: u.role === "superadmin" ? ORANGE : "#4ade80", background: u.role === "superadmin" ? "rgba(255,107,0,.1)" : "rgba(74,222,128,.1)", padding: "3px 10px", borderRadius: 99 }}>{u.role}</div>
                </div>
                <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 8 }}>LOCALES ASIGNADOS</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                  {(u.locales || []).filter(l => l.id).map(l => (
                    <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 6, background: "#0d0d0d", border: `1px solid ${CARD_BORDER}`, borderRadius: 8, padding: "5px 10px", fontSize: 12 }}>
                      <span>{l.nombre}</span>
                      {u.role !== "superadmin" && <button onClick={() => revokeLocale(u.id, l.id)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14, padding: 0 }}>×</button>}
                    </div>
                  ))}
                  {(u.locales || []).filter(l => l.id).length === 0 && u.role !== "superadmin" && <span style={{ fontSize: 11, color: TEXT_DIM }}>Sin locales</span>}
                  {u.role === "superadmin" && <span style={{ fontSize: 11, color: TEXT_DIM, fontStyle: "italic" }}>Acceso total</span>}
                </div>
                {u.role !== "superadmin" && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {allLocales.filter(l => !(u.locales || []).some(ul => ul.id === l.id)).map(l => (
                      <button key={l.id} onClick={() => assignLocale(u.id, l.id)}
                        style={{ padding: "4px 10px", background: "none", border: `1px solid rgba(255,107,0,.3)`, borderRadius: 8, color: ORANGE, fontSize: 11, cursor: "pointer" }}>+ {l.nombre}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── DASHBOARD ───────────────────────────────────────────────────
const TABS = ["Hoy", "Semana", "Mes", "Productos", "Proyección", "Gastos"];

function Dashboard({ local, slug, onLogout }) {
  const [activeTab, setActiveTab] = useState("Hoy");
  const [liveOn, setLiveOn] = useState(true);
  const [ventas, setVentas] = useState(null);
  const [semana, setSemana] = useState(null);
  const [mes, setMes] = useState(null);
  const [mesFecha, setMesFecha] = useState(null);
  const [productosMes, setProductosMes] = useState(null);
  const [loadingPM, setLoadingPM] = useState(false);
  const [categorias, setCategorias] = useState(null);
  const [loadingCat, setLoadingCat] = useState(false);
  const [gastos, setGastos] = useState(null);
  const [loadingG, setLoadingG] = useState(false);
  const [clima, setClima] = useState(null);
  const [loadingV, setLoadingV] = useState(true);
  const [loadingS, setLoadingS] = useState(false);
  const [loadingM, setLoadingM] = useState(false);

  const [hoveredBar, setHoveredBar] = useState(null);
  const [hoveredSem, setHoveredSem] = useState(null);

  useEffect(() => { const t = setInterval(() => setLiveOn(v => !v), 1400); return () => clearInterval(t); }, []);

  // Cargar ventas hoy
  useEffect(() => {
    fetch(`${API}/${slug}/ventas/hoy`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setVentas(d); setLoadingV(false); })
      .catch(() => setLoadingV(false));
  }, [slug]);

  // Refrescar ventas cada 2 minutos
  useEffect(() => {
    const t = setInterval(() => {
      fetch(`${API}/${slug}/ventas/hoy`)
        .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(setVentas)
        .catch(() => {});
    }, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [slug]);

  const currency = ventas?.currency || local.currency || "ARS";
  const isToast = currency === "USD";
  const isMaxirest = local.pos_type === "maxirest";

  useEffect(() => {
    if (local.lat && local.lon) fetchClima(local.lat, local.lon, currency).then(setClima).catch(() => {});
  }, [local, currency]);

  useEffect(() => {
    const hoyStr = new Date().toISOString().slice(0, 10);

    if (activeTab === "Semana" && !semana) {
      setLoadingS(true);
      fetch(`${API}/${slug}/ventas/semana`).then(r => r.json()).then(d => { setSemana(d); setLoadingS(false); }).catch(() => setLoadingS(false));
    }
    // Mes: recargar si no hay datos o si cambio el dia
    if (activeTab === "Mes" && (!mes || mesFecha !== hoyStr)) {
      setLoadingM(true);
      setProductosMes(null); // resetear productos al cambiar de dia
      fetch(`${API}/${slug}/ventas/mes`).then(r => r.json()).then(d => {
        setMes(d);
        setMesFecha(hoyStr);
        setLoadingM(false);
        // Cargar top productos del mes por separado (puede tardar)
        setLoadingPM(true);
        fetch(`${API}/${slug}/productos/mes`).then(r => r.json()).then(pm => {
          setProductosMes(pm.productos);
          setLoadingPM(false);
        }).catch(() => setLoadingPM(false));
      }).catch(() => setLoadingM(false));
    }
    if (activeTab === "Productos" && !categorias && !isMaxirest) {
      setLoadingCat(true);
      fetch(`${API}/${slug}/productos/categorias`).then(r => r.json()).then(d => {
        setCategorias(d.categorias);
        setLoadingCat(false);
      }).catch(() => setLoadingCat(false));
    }
    if (activeTab === "Gastos" && !gastos) {
      setLoadingG(true);
      fetch(`${API}/${slug}/gastos/mes`).then(r => r.json()).then(d => {
        setGastos(d);
        setLoadingG(false);
      }).catch(() => setLoadingG(false));
    }
  }, [activeTab, semana, mes, mesFecha, categorias, gastos, slug]);

  const now = new Date();
  const fechaStr = now.toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short" }).toUpperCase();
  const horaStr = now.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) + "hs";
  const maxBarVal = semana ? Math.max(...semana.dias.map(d => d.total), 1) : 1;
  const maxSemVal = mes ? Math.max(...mes.semanas.map(s => s.total), 1) : 1;
  const climaCode = clima?.current?.weathercode ?? 0;
  const climaActual = climaInfo(climaCode);
  const tempHoy = clima ? Math.round(clima.current?.temperature_2m ?? 0) : null;
  const lluviaHoy = clima ? (clima.current?.precipitation ?? 0) : 0;
  const climaSemana = clima?.daily ? clima.daily.time.map((fecha, i) => ({
    fecha, code: clima.daily.weathercode[i],
    tmax: Math.round(clima.daily.temperature_2m_max[i]),
    tmin: Math.round(clima.daily.temperature_2m_min[i]),
    lluvia: clima.daily.precipitation_sum[i],
  })) : [];

  // Comparativas de tickets y ticket promedio para el mes
  const ticketPct = mes && mes.tickets_anterior > 0
    ? Math.round((mes.tickets_actual - mes.tickets_anterior) / mes.tickets_anterior * 100 * 10) / 10
    : 0;
  const promPct = mes && mes.ticket_prom_ant > 0
    ? Math.round((mes.ticket_promedio - mes.ticket_prom_ant) / mes.ticket_prom_ant * 100 * 10) / 10
    : 0;

  const CSS = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Serif+Display&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    .tab-btn{background:none;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:500;color:#3a3a3a;padding:13px 0;transition:color .2s;position:relative;white-space:nowrap}
    .tab-btn.active{color:#fff}
    .tab-btn.active::after{content:'';position:absolute;bottom:0;left:0;right:0;height:2px;background:${ORANGE};border-radius:2px 2px 0 0}
    .card{background:#161616;border:1px solid ${CARD_BORDER};border-radius:14px;overflow:hidden}
    .card-v{background:#161616;border:1px solid ${CARD_BORDER};border-radius:14px;overflow:visible}
    .product-row{display:flex;align-items:center;gap:11px;padding:12px 18px;border-bottom:1px solid #1a1a1a}
    .product-row:last-child{border-bottom:none}
    .orange-chip{display:inline-flex;align-items:center;gap:4px;background:rgba(255,107,0,.12);color:${ORANGE};border:1px solid rgba(255,107,0,.2);padding:3px 9px;border-radius:99px;font-size:11px;font-weight:700}
    .red-chip{display:inline-flex;align-items:center;gap:4px;background:rgba(239,68,68,.12);color:#ef4444;border:1px solid rgba(239,68,68,.2);padding:3px 9px;border-radius:99px;font-size:11px;font-weight:700}
    @keyframes fadeSlide{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
    .anim{animation:fadeSlide .35s ease both}
    .d1{animation-delay:.05s}.d2{animation-delay:.1s}.d3{animation-delay:.15s}
    .divider{width:1px;background:#2a2a2a;align-self:stretch}
  `;

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',sans-serif", background: DARK, minHeight: "100vh", color: "#fff", display: "flex", justifyContent: "center", paddingBottom: 48 }}>
      <style>{CSS}</style>
      <div style={{ width: "100%", maxWidth: 680 }}>

        {/* HEADER */}
        <div style={{ background: DARK, borderBottom: `1px solid ${CARD_BORDER}`, padding: "16px 18px 0", position: "sticky", top: 0, zIndex: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div style={{ width: 36, height: 36, background: "#141414", border: `1px solid ${CARD_BORDER}`, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <WaveIcon size={20} />
              </div>
              <div>
                <div style={{ fontSize: 14.5, lineHeight: 1.2 }}>
                  <span style={{ fontWeight: 700, color: "#fff" }}>resto</span>
                  <span style={{ fontWeight: 300, color: ORANGE }}>mático</span>
                </div>
                <div style={{ fontSize: 10.5, color: TEXT_DIM, marginTop: 1 }}>{local.nombre}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {clima && activeTab !== "Hoy" && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#161616", border: `1px solid ${CARD_BORDER}`, borderRadius: 99, padding: "5px 11px" }}>
                  <ClimaIcono code={climaCode} size={16} />
                  <span style={{ fontWeight: 600, color: "#ccc", fontSize: 12 }}>{tempHoy}°</span>
                  {lluviaHoy > 0 && <span style={{ fontSize: 10, color: "#6bb5ff" }}>{lluviaHoy}mm</span>}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9.5, fontWeight: 800, letterSpacing: "1.8px", color: ORANGE, background: "rgba(255,107,0,.07)", border: "1px solid rgba(255,107,0,.18)", borderRadius: 99, padding: "4px 10px" }}>
                <span style={{ width: 5.5, height: 5.5, borderRadius: "50%", background: ORANGE, display: "inline-block", opacity: liveOn ? 1 : 0.15, transition: "opacity .25s" }} />
                EN VIVO
              </div>
              <button onClick={onLogout} style={{ background: "none", border: "none", color: TEXT_DIM, cursor: "pointer", fontSize: 18 }}>⊗</button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            {TABS.map(t => (
              <button key={t} className={`tab-btn ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>{t}</button>
            ))}
          </div>
        </div>

        {/* ── HOY ── */}
        {activeTab === "Hoy" && (
          <div style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 13 }}>
            <div className="anim" style={{ padding: "0 3px" }}>
              <span style={{ fontSize: 11, color: TEXT_DIM, fontWeight: 500, letterSpacing: ".6px" }}>{fechaStr} · {horaStr}</span>
            </div>
            <div className="anim d1" style={{ display: "grid", gridTemplateColumns: clima ? "1fr 130px" : "1fr", gap: 13 }}>
              <div className="card" style={{ padding: "22px 22px 20px", background: "linear-gradient(135deg,#161616 0%,#190e00 100%)", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", right: -30, top: -30, width: 140, height: 140, background: "radial-gradient(circle,rgba(255,107,0,.10) 0%,transparent 70%)", pointerEvents: "none" }} />
                {loadingV ? <div style={{ fontSize: 13, color: TEXT_DIM }}>Cargando...</div> : !ventas ? (
                  <div style={{ fontSize: 12, color: "#ef4444", padding: "8px 0" }}>Error al cargar datos — verificar conexión con el POS</div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                      <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "2px" }}>HOY · {ventas.dia_semana?.toUpperCase()}</div>
                      <div style={{ fontSize: 10, color: TEXT_DIM }}>vs <span style={{ color: TEXT_MID, fontWeight: 600 }}>{ventas.dia_semana_ant}</span> pasado</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr 1px 1fr", alignItems: "start" }}>
                      <div style={{ paddingRight: 14 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>VENTAS</div>
                        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, color: "#fff", lineHeight: 1, marginBottom: 6 }}>
                          <span style={{ fontSize: 12, verticalAlign: "super", color: "#444", marginRight: 1 }}>{currSymbol(currency)}</span>
                          <AnimatedNumber value={ventas.ventas_total} />
                        </div>
                        <Chip pct={ventas.crecimiento_pct} />
                        <div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 4 }}>{currSymbol(currency)}{fmtMoney(ventas.ventas_sem_ant, currency)}</div>
                      </div>
                      <div style={{ background: "rgba(255,255,255,.06)", alignSelf: "stretch", margin: "0 2px" }} />
                      <div style={{ padding: "0 14px" }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>TICKETS</div>
                        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, color: "#fff", lineHeight: 1, marginBottom: 6 }}>
                          <AnimatedNumber value={ventas.tickets} />
                        </div>
                        {ventas.tickets_ant > 0 && <Chip pct={Math.round((ventas.tickets - ventas.tickets_ant) / ventas.tickets_ant * 100 * 10) / 10} />}
                        <div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 4 }}>vs {ventas.tickets_ant} tickets</div>
                      </div>
                      <div style={{ background: "rgba(255,255,255,.06)", alignSelf: "stretch", margin: "0 2px" }} />
                      <div style={{ paddingLeft: 14 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>PROM. TICKET</div>
                        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, color: "#fff", lineHeight: 1, marginBottom: 6 }}>
                          <span style={{ fontSize: 12, verticalAlign: "super", color: "#444", marginRight: 1 }}>{currSymbol(currency)}</span>
                          <AnimatedNumber value={ventas.ticket_promedio} />
                        </div>
                        {ventas.tickets_ant > 0 && ventas.ventas_sem_ant > 0 && (() => {
                          const promAnt = Math.round(ventas.ventas_sem_ant / ventas.tickets_ant);
                          const promPctHoy = Math.round((ventas.ticket_promedio - promAnt) / promAnt * 100 * 10) / 10;
                          return <><Chip pct={promPctHoy} /><div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 4 }}>{currSymbol(currency)}{fmtMoney(promAnt, currency)}</div></>;
                        })()}
                        {(!ventas.tickets_ant || !ventas.ventas_sem_ant) && <div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 4 }}>por ticket</div>}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {clima && (
                <div className="card" style={{ padding: "20px 14px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px" }}>CLIMA</div>
                  <ClimaIcono code={climaCode} size={38} />
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#fff", lineHeight: 1 }}>{tempHoy}{tempUnit(currency)}</div>
                  <div style={{ fontSize: 11, color: TEXT_MID }}>{climaActual.label}</div>
                  {lluviaHoy > 0 && <div style={{ fontSize: 10, color: "#6bb5ff", fontWeight: 600 }}>{lluviaHoy}mm</div>}
                  {climaActual.lluvia && <div style={{ fontSize: 9, color: "#6bb5ff", fontStyle: "italic" }}>Puede impactar</div>}
                </div>
              )}
            </div>
            {ventas && ventas.tickets > 0 && (
              <>
                {ventas.turnos && (
                  <div className="card anim d2" style={{ padding: "18px" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px", marginBottom: 14 }}>
                      {isToast ? "TURNOS" : "TURNOS DEL DÍA"}
                    </div>
                    {(isToast || isMaxirest) ? (
                      /* Toast / MaxiRest: dynamic dayparts */
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(Object.keys(ventas.turnos).length, 3)}, 1fr)`, gap: 0 }}>
                        {Object.entries(ventas.turnos).map(([key, turno], i) => {
                          const activo = turno && turno.tickets > 0;
                          const sym = currSymbol(currency);
                          return (
                            <div key={key} style={{ padding: i === 0 ? "0 12px 0 0" : "0 0 0 12px", borderLeft: i > 0 ? "1px solid rgba(255,255,255,.06)" : "none" }}>
                              <div style={{ fontSize: 9, fontWeight: 800, color: activo ? TEXT_DIM : "#333", letterSpacing: "1.2px", marginBottom: 6 }}>{turno.label?.toUpperCase() || key.toUpperCase()}</div>
                              <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, color: activo ? "#fff" : "#2a2a2a", lineHeight: 1, marginBottom: 4 }}>
                                {activo ? <><span style={{ fontSize: 10, verticalAlign: "super", color: "#444", marginRight: 1 }}>{sym}</span>{fmtMoney(turno.total, currency)}</> : "—"}
                              </div>
                              <div style={{ fontSize: 9.5, color: activo ? TEXT_MID : "#333" }}>{activo ? `${turno.tickets} tickets` : "—"}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Fudo: fixed 3 turnos */
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
                        {[
                          { key: "desayuno", label: "DESAYUNO", horario: "7-12hs" },
                          { key: "almuerzo", label: "ALMUERZO", horario: "12-16hs" },
                          { key: "merienda", label: "MERIENDA", horario: "16-20hs" },
                        ].map((t, i) => {
                          const turno = ventas.turnos[t.key];
                          const activo = turno && turno.tickets > 0;
                          return (
                            <div key={t.key} style={{ padding: i === 0 ? "0 12px 0 0" : "0 0 0 12px", borderLeft: i > 0 ? "1px solid rgba(255,255,255,.06)" : "none" }}>
                              <div style={{ fontSize: 9, fontWeight: 800, color: activo ? TEXT_DIM : "#333", letterSpacing: "1.2px", marginBottom: 6 }}>{t.label}</div>
                              <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, color: activo ? "#fff" : "#2a2a2a", lineHeight: 1, marginBottom: 4 }}>
                                {activo ? <><span style={{ fontSize: 10, verticalAlign: "super", color: "#444", marginRight: 1 }}>$</span>{fmt(turno.total)}</> : "—"}
                              </div>
                              <div style={{ fontSize: 9.5, color: activo ? TEXT_MID : "#333" }}>{activo ? `${turno.tickets} tickets` : t.horario}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {/* Toast extras: discounts + voids */}
                {isToast && ventas.discounts != null && (ventas.discounts > 0 || ventas.voids > 0) && (
                  <div className="card anim d2" style={{ padding: "18px" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px", marginBottom: 12 }}>DESCUENTOS & ANULACIONES</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.2px", marginBottom: 4 }}>DISCOUNTS</div>
                        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, color: ventas.discounts > 0 ? "#ef4444" : "#2a2a2a" }}>
                          {ventas.discounts > 0 ? <><span style={{ fontSize: 10, color: "#666" }}>USD </span>{fmtUSD(ventas.discounts)}</> : "—"}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.2px", marginBottom: 4 }}>VOIDS</div>
                        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, color: ventas.voids > 0 ? "#ef4444" : "#2a2a2a" }}>
                          {ventas.voids > 0 ? ventas.voids : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="card anim d3" style={{ padding: "18px" }}>
                  <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px", marginBottom: 12 }}>
                    {isToast ? "CANAL DE VENTA" : isMaxirest ? "FORMA DE COBRO" : "TIPO DE SERVICIO"}
                  </div>
                  {isToast ? (
                    <>
                      <div style={{ display: "flex", gap: 2, height: 6, borderRadius: 99, overflow: "hidden", marginBottom: 10 }}>
                        <div style={{ width: `${((ventas.on_prem || 0) / ventas.tickets) * 100}%`, background: ORANGE, transition: "width 1s" }} />
                        <div style={{ flex: 1, background: "#2a2a2a" }} />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 11, color: TEXT_MID }}>On-Prem {ventas.on_prem || 0} ({Math.round((ventas.on_prem || 0) / ventas.tickets * 100)}%)</span>
                        <span style={{ fontSize: 11, color: TEXT_MID }}>Online {ventas.online || 0} ({Math.round((ventas.online || 0) / ventas.tickets * 100)}%)</span>
                      </div>
                    </>
                  ) : isMaxirest ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
                      {[
                        { key: "efectivo", label: "EFECTIVO", value: ventas.efectivo || 0 },
                        { key: "mercadopago", label: "MERCADOPAGO", value: ventas.mercadopago || 0 },
                        { key: "pedidosya", label: "PEYA", value: ventas.pedidosya || 0 },
                      ].map(({ key, label, value }, i) => (
                        <div key={key} style={{ padding: i === 0 ? "0 12px 0 0" : "0 0 0 12px", borderLeft: i > 0 ? "1px solid rgba(255,255,255,.06)" : "none" }}>
                          <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.2px", marginBottom: 6 }}>{label}</div>
                          <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, color: value > 0 ? "#fff" : "#2a2a2a", lineHeight: 1, marginBottom: 4 }}>
                            {value > 0 ? <><span style={{ fontSize: 10, verticalAlign: "super", color: "#444", marginRight: 1 }}>$</span>{fmt(value)}</> : "—"}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 2, height: 6, borderRadius: 99, overflow: "hidden", marginBottom: 10 }}>
                        <div style={{ width: `${(ventas.salon / ventas.tickets) * 100}%`, background: ORANGE, transition: "width 1s" }} />
                        <div style={{ flex: 1, background: "#2a2a2a" }} />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 11, color: TEXT_MID }}>Salon {ventas.salon} ({Math.round(ventas.salon / ventas.tickets * 100)}%)</span>
                        <span style={{ fontSize: 11, color: TEXT_MID }}>Takeaway {ventas.takeaway} ({Math.round(ventas.takeaway / ventas.tickets * 100)}%)</span>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── SEMANA ── */}
        {activeTab === "Semana" && (
          <div style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 13 }}>
            <div className="anim" style={{ padding: "0 3px" }}><span style={{ fontSize: 11, color: TEXT_DIM, fontWeight: 500, letterSpacing: ".6px" }}>ULTIMOS 7 DIAS</span></div>
            {loadingS ? <div style={{ textAlign: "center", padding: "40px 0", color: TEXT_DIM, fontSize: 13 }}>Cargando semana...</div> : semana ? (
              <>
                <div className="card anim d1" style={{ padding: "22px 22px 20px", background: "linear-gradient(135deg,#161616 0%,#190e00 100%)", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", right: -30, top: -30, width: 140, height: 140, background: "radial-gradient(circle,rgba(255,107,0,.10) 0%,transparent 70%)", pointerEvents: "none" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "2px" }}>ÚLTIMOS 7 DÍAS</div>
                    <div style={{ fontSize: 10, color: TEXT_DIM }}>vs <span style={{ color: TEXT_MID, fontWeight: 600 }}>7 días previos</span></div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr 1px 1fr", alignItems: "start" }}>
                    <div style={{ paddingRight: 14 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>VENTAS</div>
                      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, color: "#fff", lineHeight: 1, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, verticalAlign: "super", color: "#444", marginRight: 1 }}>{currSymbol(currency)}</span>{fmtMoney(semana.total_semana, currency)}
                      </div>
                      <Chip pct={semana.crecimiento_pct} />
                      <div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 3 }}>{currSymbol(currency)}{fmtMoney(semana.total_semana_ant, currency)}</div>
                    </div>
                    <div style={{ background: "rgba(255,255,255,.06)", alignSelf: "stretch", margin: "0 2px" }} />
                    <div style={{ padding: "0 14px" }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>TICKETS</div>
                      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, color: "#fff", lineHeight: 1, marginBottom: 6 }}>{fmt(semana.total_tickets)}</div>
                      {semana.total_tickets_ant > 0 && <Chip pct={Math.round((semana.total_tickets - semana.total_tickets_ant) / semana.total_tickets_ant * 100 * 10) / 10} />}
                      <div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 3 }}>{fmt(semana.total_tickets_ant)} prev.</div>
                    </div>
                    <div style={{ background: "rgba(255,255,255,.06)", alignSelf: "stretch", margin: "0 2px" }} />
                    <div style={{ paddingLeft: 14 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>PROM. TICKET</div>
                      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, color: "#fff", lineHeight: 1, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, verticalAlign: "super", color: "#444", marginRight: 1 }}>{currSymbol(currency)}</span>{fmtMoney(semana.ticket_promedio, currency)}
                      </div>
                      <div style={{ fontSize: 9.5, color: TEXT_DIM }}>mejor: {semana.mejor_dia}</div>
                    </div>
                  </div>
                </div>
                <div className="card-v anim d3" style={{ padding: "20px 16px 16px" }}>
                  <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px", marginBottom: 14 }}>VENTAS + CLIMA POR DIA</div>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 120, marginBottom: 10 }}>
                    {semana.dias.map((d, i) => {
                      const h = maxBarVal > 0 ? Math.round((d.total / maxBarVal) * 100) : 0;
                      const isHov = hoveredBar === i;
                      const climaDia = climaSemana.find(c => c.fecha === d.fecha);
                      return (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", position: "relative", cursor: "pointer" }}
                          onMouseEnter={() => setHoveredBar(i)} onMouseLeave={() => setHoveredBar(null)}>
                          {isHov && (
                            <div style={{ position: "absolute", bottom: "105%", left: "50%", transform: "translateX(-50%)", background: "#1a1a1a", border: `1px solid #333`, borderRadius: 10, padding: "8px 12px", whiteSpace: "nowrap", zIndex: 50, fontSize: 11, color: "#fff", lineHeight: 1.8, pointerEvents: "none", boxShadow: "0 4px 20px rgba(0,0,0,.6)" }}>
                              <div style={{ fontWeight: 700, color: d.hoy ? ORANGE : "#fff" }}>{d.label}</div>
                              <div style={{ color: ORANGE, fontWeight: 600 }}>{currSymbol(currency)}{fmtMoney(d.total, currency)}</div>
                              <div style={{ color: TEXT_MID }}>{d.tickets} tickets</div>
                              {climaDia && <div style={{ color: "#bbb", display: "flex", alignItems: "center", gap: 5 }}><ClimaIcono code={climaDia.code} size={12} /> {climaDia.tmax}° / {climaDia.tmin}°</div>}
                              {climaDia && climaDia.lluvia > 0 && <div style={{ color: "#6bb5ff", fontSize: 10 }}>{climaDia.lluvia}mm lluvia</div>}
                            </div>
                          )}
                          <div style={{ width: "100%", display: "flex", alignItems: "flex-end", height: "100%" }}>
                            <div style={{ width: "100%", height: `${h}%`, minHeight: d.total > 0 ? 4 : 0, background: d.hoy ? `linear-gradient(to top,${ORANGE},#ff9240)` : isHov ? "#333" : "#242424", borderRadius: "4px 4px 0 0", transition: "height .6s cubic-bezier(.4,0,.2,1)" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ height: 1, background: "#1e1e1e", marginBottom: 12 }} />
                  <div style={{ display: "flex", gap: 8 }}>
                    {semana.dias.map((d, i) => {
                      const climaDia = climaSemana.find(c => c.fecha === d.fecha);
                      return (
                        <div key={i} style={{ flex: 1, textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: d.hoy ? ORANGE : TEXT_DIM, fontWeight: d.hoy ? 800 : 400, marginBottom: 6 }}>{d.label}</div>
                          <div style={{ display: "flex", justifyContent: "center" }}>
                            {climaDia ? <><ClimaIcono code={climaDia.code} size={18} /><div style={{ fontSize: 8, color: TEXT_DIM, marginTop: 2 }}>{climaDia.tmax}°</div></> : <span style={{ display: "inline-block", width: 18, height: 18 }} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {semana.total_tickets > 0 && !isMaxirest && (
                  <div className="card anim" style={{ padding: "16px 18px" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px", marginBottom: 12 }}>TIPO DE SERVICIO</div>
                    <div style={{ display: "flex", gap: 2, height: 6, borderRadius: 99, overflow: "hidden", marginBottom: 10 }}>
                      <div style={{ width: `${(semana.total_salon / semana.total_tickets) * 100}%`, background: ORANGE, transition: "width 1s" }} />
                      <div style={{ flex: 1, background: "#2a2a2a" }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 11, color: TEXT_MID }}>Salon {semana.total_salon} ({Math.round(semana.total_salon / semana.total_tickets * 100)}%)</span>
                      <span style={{ fontSize: 11, color: TEXT_MID }}>Takeaway {semana.total_takeaway} ({Math.round(semana.total_takeaway / semana.total_tickets * 100)}%)</span>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* ── MES ── */}
        {activeTab === "Mes" && (
          <div style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 13 }}>
            <div className="anim" style={{ padding: "0 3px" }}>
              <span style={{ fontSize: 11, color: TEXT_DIM, fontWeight: 500, letterSpacing: ".6px" }}>
                {mes ? `${mes.mes_actual.toUpperCase()} VS ${mes.mes_anterior.toUpperCase()}` : "ESTE MES"}
              </span>
            </div>
            {loadingM ? <div style={{ textAlign: "center", padding: "40px 0", color: TEXT_DIM, fontSize: 13 }}>Cargando mes...</div> : mes ? (
              <>
                {/* Hero — ventas + tickets + promedio en un solo card */}
                <div className="card anim d1" style={{ padding: "22px 22px 20px", background: "linear-gradient(135deg,#161616 0%,#190e00 100%)", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", right: -30, top: -30, width: 140, height: 140, background: "radial-gradient(circle,rgba(255,107,0,.10) 0%,transparent 70%)", pointerEvents: "none" }} />

                  {/* Header del card */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "2px" }}>
                      {mes.mes_actual.toUpperCase()} · {mes.dias_transcurridos} DIAS
                    </div>
                    <div style={{ fontSize: 10, color: TEXT_DIM }}>vs <span style={{ color: TEXT_MID, fontWeight: 600 }}>{mes.mes_anterior}</span></div>
                  </div>

                  {/* 3 métricas en fila */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr 1px 1fr", alignItems: "start" }}>

                    {/* Ventas */}
                    <div style={{ paddingRight: 14 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>VENTAS</div>
                      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 28, color: "#fff", lineHeight: 1, marginBottom: 6 }}>
                        <span style={{ fontSize: 13, verticalAlign: "super", color: "#444", marginRight: 1 }}>{currSymbol(currency)}</span>
                        <AnimatedNumber value={mes.total_actual} />
                      </div>
                      <Chip pct={mes.crecimiento_pct} />
                      <div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 4 }}>{currSymbol(currency)}{fmtMoney(mes.total_anterior, currency)}</div>
                    </div>

                    <div style={{ background: "rgba(255,255,255,.06)", alignSelf: "stretch", margin: "0 2px" }} />

                    {/* Tickets */}
                    <div style={{ padding: "0 14px" }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>TICKETS</div>
                      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 28, color: "#fff", lineHeight: 1, marginBottom: 6 }}>
                        {fmt(mes.tickets_actual)}
                      </div>
                      <Chip pct={ticketPct} />
                      <div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 4 }}>{fmt(mes.tickets_anterior)}</div>
                    </div>

                    <div style={{ background: "rgba(255,255,255,.06)", alignSelf: "stretch", margin: "0 2px" }} />

                    {/* Ticket promedio */}
                    <div style={{ paddingLeft: 14 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>PROM. TICKET</div>
                      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 28, color: "#fff", lineHeight: 1, marginBottom: 6 }}>
                        <span style={{ fontSize: 13, verticalAlign: "super", color: "#444", marginRight: 1 }}>{currSymbol(currency)}</span>
                        {fmtMoney(mes.ticket_promedio, currency)}
                      </div>
                      <Chip pct={promPct} />
                      <div style={{ fontSize: 9.5, color: TEXT_DIM, marginTop: 4 }}>{currSymbol(currency)}{fmtMoney(mes.ticket_prom_ant, currency)}</div>
                    </div>
                  </div>
                </div>

                {/* Grafico por semanas */}
                <div className="card-v anim d2" style={{ padding: "20px 16px 16px" }}>
                  <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px", marginBottom: 14 }}>VENTAS POR SEMANA</div>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 110, marginBottom: 10 }}>
                    {mes.semanas.map((s, i) => {
                      const h = maxSemVal > 0 ? Math.round((s.total / maxSemVal) * 100) : 0;
                      const isHov = hoveredSem === i;
                      const esUltima = i === mes.semanas.length - 1;
                      return (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", position: "relative", cursor: "pointer" }}
                          onMouseEnter={() => setHoveredSem(i)} onMouseLeave={() => setHoveredSem(null)}>
                          {isHov && (
                            <div style={{ position: "absolute", bottom: "105%", left: "50%", transform: "translateX(-50%)", background: "#1a1a1a", border: `1px solid #333`, borderRadius: 10, padding: "8px 12px", whiteSpace: "nowrap", zIndex: 50, fontSize: 11, color: "#fff", lineHeight: 1.8, pointerEvents: "none", boxShadow: "0 4px 20px rgba(0,0,0,.6)" }}>
                              <div style={{ fontWeight: 700, color: esUltima ? ORANGE : "#fff" }}>{s.desde} — {s.hasta}</div>
                              <div style={{ color: ORANGE, fontWeight: 600 }}>{currSymbol(currency)}{fmtMoney(s.total, currency)}</div>
                              <div style={{ color: TEXT_MID }}>{s.tickets} tickets</div>
                            </div>
                          )}
                          <div style={{ width: "100%", display: "flex", alignItems: "flex-end", height: "100%" }}>
                            <div style={{ width: "100%", height: `${h}%`, minHeight: s.total > 0 ? 4 : 0, background: esUltima ? `linear-gradient(to top,${ORANGE},#ff9240)` : isHov ? "#333" : "#242424", borderRadius: "4px 4px 0 0", transition: "height .6s cubic-bezier(.4,0,.2,1)" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ height: 1, background: "#1e1e1e", marginBottom: 12 }} />
                  <div style={{ display: "flex", gap: 12 }}>
                    {mes.semanas.map((s, i) => {
                      const esUltima = i === mes.semanas.length - 1;
                      return (
                        <div key={i} style={{ flex: 1, textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: esUltima ? ORANGE : TEXT_DIM, fontWeight: esUltima ? 800 : 400 }}>{s.label}</div>
                          <div style={{ fontSize: 8, color: "#333", marginTop: 2 }}>{s.desde}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top productos del mes — carga separada */}
                <div className="card anim">
                  <div style={{ padding: "16px 18px 12px", borderBottom: `1px solid #1a1a1a` }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px" }}>TOP PRODUCTOS DEL MES</div>
                  </div>
                  {loadingPM ? (
                    <div style={{ padding: "20px 18px", fontSize: 12, color: TEXT_DIM }}>Calculando productos...</div>
                  ) : productosMes ? (
                    productosMes.map((p, i) => (
                      <div key={i} className="product-row">
                        <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 12, color: i < 3 ? ORANGE : "#2e2e2e", minWidth: 22 }}>{p.rank}</span>
                        <span style={{ flex: 1, fontSize: 13, color: "#e0e0e0" }}>{p.nombre}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <div style={{ height: 2, borderRadius: 99, background: "#222", width: `${(p.cantidad / productosMes[0].cantidad) * 56}px`, overflow: "hidden" }}>
                            <div style={{ height: "100%", borderRadius: 99, background: i < 3 ? ORANGE : "#2e2e2e", width: "100%" }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: i < 3 ? ORANGE : TEXT_DIM, minWidth: 26, textAlign: "right" }}>×{p.cantidad}</span>
                        </div>
                      </div>
                    ))
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* ── PRODUCTOS ── */}
        {activeTab === "Productos" && (
          <div style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 13 }}>
            <div className="anim" style={{ padding: "0 3px" }}>
              <span style={{ fontSize: 11, color: TEXT_DIM, fontWeight: 500, letterSpacing: ".6px" }}>
                {isToast ? "TOP PRODUCTOS · HOY" : isMaxirest ? "PRODUCTOS" : "TOP POR CATEGORÍA · HOY"}
              </span>
            </div>
            {isMaxirest ? (
              <div style={{ textAlign: "center", padding: "48px 0", color: TEXT_DIM }}>
                <div style={{ fontSize: 13, marginBottom: 6 }}>Sin datos de productos</div>
                <div style={{ fontSize: 11, color: "#333" }}>MaxiRest no provee detalle por producto</div>
              </div>
            ) : loadingCat ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: TEXT_DIM }}>
                <div style={{ fontSize: 13, marginBottom: 8 }}>Cargando productos...</div>
                <div style={{ fontSize: 10, color: "#333" }}>Puede tardar unos segundos</div>
              </div>
            ) : categorias ? (
              <>
                {/* Toast: flat "top" key */}
                {categorias.top && (
                  <div className="card anim">
                    <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14 }}>🏆</span>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px" }}>{categorias.top.label?.toUpperCase() || "TOP PRODUCTOS"}</span>
                    </div>
                    {categorias.top.productos?.map((p, i) => {
                      const maxCant = categorias.top.productos[0]?.cantidad || 1;
                      return (
                        <div key={i} className="product-row">
                          <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 12, color: i < 3 ? ORANGE : "#2e2e2e", minWidth: 22 }}>{p.rank}</span>
                          <span style={{ flex: 1, fontSize: 13, color: i < 3 ? "#fff" : "#aaa" }}>{p.nombre}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <div style={{ height: 2, borderRadius: 99, background: "#222", width: `${(p.cantidad / maxCant) * 48}px`, overflow: "hidden" }}>
                              <div style={{ height: "100%", borderRadius: 99, background: i < 3 ? ORANGE : "#2e2e2e", width: "100%" }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: i < 3 ? ORANGE : TEXT_DIM, minWidth: 24, textAlign: "right" }}>×{p.cantidad}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Fudo: category keys */}
                {!categorias.top && ["calientes", "salado", "dulces", "market"].map((catKey) => {
                  const cat = categorias[catKey];
                  if (!cat || cat.productos.length === 0) return null;
                  const maxCant = cat.productos[0]?.cantidad || 1;
                  const iconos = { calientes: "☕", salado: "🍳", dulces: "🥐", market: "🛒" };
                  return (
                    <div key={catKey} className="card anim">
                      <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14 }}>{iconos[catKey]}</span>
                        <span style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px" }}>{cat.label.toUpperCase()}</span>
                      </div>
                      {cat.productos.map((p, i) => (
                        <div key={i} className="product-row">
                          <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 12, color: i === 0 ? ORANGE : "#2e2e2e", minWidth: 22 }}>{p.rank}</span>
                          <span style={{ flex: 1, fontSize: 13, color: i === 0 ? "#fff" : "#aaa" }}>{p.nombre}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <div style={{ height: 2, borderRadius: 99, background: "#222", width: `${(p.cantidad / maxCant) * 48}px`, overflow: "hidden" }}>
                              <div style={{ height: "100%", borderRadius: 99, background: i === 0 ? ORANGE : "#2e2e2e", width: "100%" }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: i === 0 ? ORANGE : TEXT_DIM, minWidth: 24, textAlign: "right" }}>×{p.cantidad}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            ) : <div style={{ textAlign: "center", padding: "40px 0", color: TEXT_DIM, fontSize: 13 }}>Sin datos</div>}
          </div>
        )}

        {/* ── PROYECCIÓN ── */}
        {activeTab === "Proyección" && (
          <div style={{ padding: "18px 16px" }}>
            <SalesProjection slug={slug} currency={currency} />
          </div>
        )}

        {/* ── GASTOS ── */}
        {activeTab === "Gastos" && (
          <div style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 13 }}>
            <div className="anim" style={{ padding: "0 3px" }}>
              <span style={{ fontSize: 11, color: TEXT_DIM, fontWeight: 500, letterSpacing: ".6px" }}>
                {gastos ? `GASTOS · ${gastos.mes?.toUpperCase()} ${gastos.anio}` : "GASTOS DEL MES"}
              </span>
            </div>
            {loadingG ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: TEXT_DIM }}>
                <div style={{ fontSize: 13, marginBottom: 8 }}>Cargando gastos...</div>
                <div style={{ fontSize: 10, color: "#333" }}>Puede tardar unos segundos</div>
              </div>
            ) : gastos ? (
              <>
                {/* KPIs */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div className="card anim" style={{ padding: "16px 18px" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>TOTAL GASTOS</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#e55", fontFamily: "'DM Serif Display',serif" }}>
                      ${fmt(gastos.total)}
                    </div>
                    <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 3 }}>{gastos.count} registros</div>
                  </div>
                  {mes && mes.total_actual > 0 ? (
                    <div className="card anim" style={{ padding: "16px 18px" }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>RESULTADO BRUTO</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: mes.total_actual > gastos.total ? ORANGE : "#e55", fontFamily: "'DM Serif Display',serif" }}>
                        ${fmt(mes.total_actual - gastos.total)}
                      </div>
                      <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 3 }}>
                        {Math.round((mes.total_actual - gastos.total) / mes.total_actual * 100)}% margen
                      </div>
                    </div>
                  ) : (
                    <div className="card anim" style={{ padding: "16px 18px" }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, color: TEXT_DIM, letterSpacing: "1.4px", marginBottom: 6 }}>REGISTROS</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#888", fontFamily: "'DM Serif Display',serif" }}>
                        {gastos.count}
                      </div>
                      <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 3 }}>egresos del mes</div>
                    </div>
                  )}
                </div>

                {/* Por categoría */}
                {Object.keys(gastos.by_cat).length > 0 && (
                  <div className="card anim">
                    <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid #1a1a1a" }}>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px" }}>POR CATEGORÍA</span>
                    </div>
                    {Object.entries(gastos.by_cat).map(([catId, d], i) => {
                      const maxVal = Object.values(gastos.by_cat)[0]?.total || 1;
                      return (
                        <div key={catId} className="product-row">
                          <span style={{ flex: 1, fontSize: 13, color: i === 0 ? "#e0e0e0" : "#aaa" }}>Cat. {catId}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ height: 2, borderRadius: 99, background: "#222", width: `${Math.round((d.total / maxVal) * 60)}px`, overflow: "hidden" }}>
                              <div style={{ height: "100%", borderRadius: 99, background: "#e55", width: "100%" }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? "#e88" : TEXT_DIM, minWidth: 86, textAlign: "right" }}>${fmt(d.total)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Por medio de pago */}
                {Object.keys(gastos.by_pm).length > 0 && (
                  <div className="card anim">
                    <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid #1a1a1a" }}>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px" }}>MEDIO DE PAGO</span>
                    </div>
                    {Object.entries(gastos.by_pm).map(([pm, d], i) => {
                      const maxVal = Object.values(gastos.by_pm)[0]?.total || 1;
                      return (
                        <div key={pm} className="product-row">
                          <span style={{ flex: 1, fontSize: 13, color: i === 0 ? "#e0e0e0" : "#aaa" }}>{pm}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ height: 2, borderRadius: 99, background: "#222", width: `${Math.round((d.total / maxVal) * 60)}px`, overflow: "hidden" }}>
                              <div style={{ height: "100%", borderRadius: 99, background: "#e55", width: "100%" }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? "#e88" : TEXT_DIM, minWidth: 86, textAlign: "right" }}>${fmt(d.total)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Evolución diaria */}
                {Object.keys(gastos.by_day).length > 0 && (
                  <div className="card anim">
                    <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid #1a1a1a" }}>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: TEXT_DIM, letterSpacing: "1.8px" }}>EVOLUCIÓN DIARIA</span>
                    </div>
                    <div style={{ padding: "14px 14px 10px", display: "flex", gap: 3, alignItems: "flex-end", overflowX: "auto" }}>
                      {(() => {
                        const days = Object.entries(gastos.by_day);
                        const maxDay = Math.max(...days.map(([, v]) => v), 1);
                        return days.map(([date, amt]) => {
                          const barH = Math.max(Math.round((amt / maxDay) * 52), 2);
                          return (
                            <div key={date} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, flex: "0 0 auto" }}>
                              <div style={{ width: 16, height: 52, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                                <div style={{ width: "100%", height: barH, background: "#c44", borderRadius: "2px 2px 0 0", opacity: 0.85 }} />
                              </div>
                              <span style={{ fontSize: 8.5, color: TEXT_MID }}>{date.slice(8)}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </>
            ) : <div style={{ textAlign: "center", padding: "40px 0", color: TEXT_DIM, fontSize: 13 }}>Sin datos</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ROUTER ──────────────────────────────────────────────────────
export default function App() {
  const path = window.location.pathname.replace(/^\//, "").replace(/\/$/, "");

  const [session, setSession] = useState(null);
  const [selectedLocal, setSelectedLocal] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("rm_user");
    const loginTime = localStorage.getItem("rm_login_time");
    // Auto-logout after 24h
    if (loginTime && Date.now() - parseInt(loginTime) > 24 * 60 * 60 * 1000) {
      localStorage.removeItem("rm_user"); localStorage.removeItem("rm_selected_local"); localStorage.removeItem("rm_login_time");
      setChecked(true); return;
    }
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setSession(data);
        // If URL has a slug, find and select that local
        if (path && path !== "app" && path !== "admin") {
          const match = data.locales.find(l => l.slug === path);
          if (match) { setSelectedLocal(match); }
        } else {
          const savedLocal = localStorage.getItem("rm_selected_local");
          if (savedLocal) { try { setSelectedLocal(JSON.parse(savedLocal)); } catch(e) {} }
        }
      } catch(e) {}
    }
    setChecked(true);
  }, [path]);

  const handleLogin = (data) => { setSession(data); };
  const handleLogout = () => { localStorage.removeItem("rm_user"); localStorage.removeItem("rm_selected_local"); localStorage.removeItem("rm_login_time"); setSession(null); setSelectedLocal(null); setShowAdmin(false); window.history.pushState({}, "", "/app"); };
  const handleSelectLocal = (local) => { localStorage.setItem("rm_selected_local", JSON.stringify(local)); setSelectedLocal(local); window.history.pushState({}, "", "/" + local.slug); };
  const handleBackToLocales = () => { localStorage.removeItem("rm_selected_local"); setSelectedLocal(null); setShowAdmin(false); window.history.pushState({}, "", "/app"); };

  if (path.startsWith("investor/")) {
    const slug = path.split("/")[1];
    return <InvestorDashboard slug={slug} />;
  }

  if (!checked) return null;
  if (!session) return <LoginScreen onLogin={handleLogin} />;
  if (showAdmin && session.user.role === "superadmin") return <AdminPanel user={session.user} onBack={() => setShowAdmin(false)} />;
  if (!selectedLocal) {
    if (session.locales.length === 1) { handleSelectLocal(session.locales[0]); return null; }
    return <LocalSelector user={session.user} locales={session.locales} onSelect={handleSelectLocal} onLogout={handleLogout} onAdmin={() => setShowAdmin(true)} />;
  }
  return <Dashboard local={selectedLocal} slug={selectedLocal.slug} onLogout={handleBackToLocales} />;
}
